// 003 — Hospitalización / Anamnesis-Examen Físico (Anexo 1 MSP, SNS-MSP/HCU-form.003/2021). Fase 7, Tanda 17: bloques B-K
// de las dos hojas (6 motivos, antecedentes de 11 categorías + 18 campos gineco-obstétricos/andrológicos, familiares,
// enfermedad actual, revisión de órganos, constantes de una toma, examen físico, análisis, 6 diagnósticos PRE/DEF, plan).
// Datos y decisiones en `bloques003Datos.ts` / `bloquesAnamnesisDatos.ts`. La silueta corporal no se transcribe. Al guardar
// deja `Encounter.reasonCode`, `Condition`s, el `Observation` de hallazgos, las `AllergyIntolerance` (alergias marcadas) y la
// `Composition` 003 que marca la anamnesis como completada.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { Bloque } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import { SeccionCampos } from './SeccionCampos';
import { SiluetaCorporal } from './SiluetaCorporal';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import {
  CAMPOS_ENFERMEDAD_ANAMNESIS,
  CAMPOS_EXAMEN_REGIONAL,
  CAMPOS_EXAMEN_SISTEMICO,
  CAMPOS_FAMILIARES_ANAMNESIS,
  CAMPOS_REVISION_ANAMNESIS,
  codigosDiagnostico,
} from './bloquesAnamnesisDatos';
import {
  alergiasDe003,
  CAMPOS_ANALISIS_003,
  CAMPOS_CONSTANTES_003,
  CAMPOS_GINECO_003,
  CAMPOS_MOTIVOS_003,
  CAMPOS_PERSONALES_003,
  CAMPOS_PLAN_003,
  estadoInicial003,
  hallazgosDe003,
  motivoDe003,
  paresDe003,
  siluetaDe003,
  tomasDe003,
  type Estado003,
} from './bloques003Datos';
import { guardarRecursosAnamnesis } from './guardadoAnamnesis';

type Clave = 'motivos' | 'personales' | 'gineco' | 'familiares' | 'enfermedad' | 'revision' | 'constantes' | 'regional' | 'sistemico' | 'analisis' | 'plan';

export function Formulario003({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado003>(() => estadoInicial003());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): HOSPITALIZACIÓN - ANAMNESIS: el papel no trae servicio; es hospitalario, no consulta externa.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '003', t('formularios.f003.titulo'), { servicio: 'Hospitalización' });

  const seccion = (clave: Clave, prefijo: string, lista: readonly CampoSimple[], columnas: 2 | 3 | 4, filas = 3): JSX.Element => (
    <SeccionCampos prefijoId={`f003-${prefijo}`} campos={lista} valores={estado[clave]} columnas={columnas} filasArea={filas} onChange={(v) => setEstado({ ...estado, [clave]: v })} />
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      await guardarRecursosAnamnesis(medplum, {
        codigo: '003',
        subject,
        encounter,
        motivo: motivoDe003(estado),
        diagnosticos: codigosDiagnostico(estado.diagnosticos),
        hallazgos: hallazgosDe003(estado),
        alergias: alergiasDe003(estado),
        tomas: tomasDe003(estado),
      });
      onGuardado(paresDe003(estado), datosEncabezadoMsp, undefined, undefined, undefined, undefined, undefined, undefined, siluetaDe003(estado));
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f003.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f003.motivoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f003.motivoNota')}</p>
        {seccion('motivos', 'mot', CAMPOS_MOTIVOS_003, 2, 2)}
      </Bloque>
      <Bloque titulo={t('formularios.f003.personalesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f003.describaNota')}</p>
        {seccion('personales', 'per', CAMPOS_PERSONALES_003, 4, 3)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f003.ginecoTitulo')}</p>
        <p className="text-xs text-hce-muted">{t('formularios.f003.ginecoNota')}</p>
        {seccion('gineco', 'gin', CAMPOS_GINECO_003, 4)}
      </Bloque>
      <Bloque titulo={t('formularios.f003.familiaresTitulo')}>{seccion('familiares', 'fam', CAMPOS_FAMILIARES_ANAMNESIS, 4, 3)}</Bloque>
      <Bloque titulo={t('formularios.f003.enfermedadTitulo')}>{seccion('enfermedad', 'enf', CAMPOS_ENFERMEDAD_ANAMNESIS, 2, 6)}</Bloque>
      <Bloque titulo={t('formularios.f003.revisionTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f003.marcarNota')}</p>
        {seccion('revision', 'rev', CAMPOS_REVISION_ANAMNESIS, 4, 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f003.constantesTitulo')}>{seccion('constantes', 'con', CAMPOS_CONSTANTES_003, 4)}</Bloque>
      <Bloque titulo={t('formularios.f003.examenTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f003.marcarNota')}</p>
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f003.regional')}</p>
        {seccion('regional', 'reg', CAMPOS_EXAMEN_REGIONAL, 4, 3)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f003.sistemico')}</p>
        {seccion('sistemico', 'sis', CAMPOS_EXAMEN_SISTEMICO, 4, 3)}
        <p className="text-sm font-medium text-hce-ink">{t('formularios.f003.siluetaTitulo')}</p>
        <SiluetaCorporal regiones={estado.silueta} onChange={(silueta) => setEstado({ ...estado, silueta })} />
      </Bloque>
      <Bloque titulo={t('formularios.f003.analisisTitulo')}>{seccion('analisis', 'ana', CAMPOS_ANALISIS_003, 2, 5)}</Bloque>

      <Bloque titulo={t('formularios.f003.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f003.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f003.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f003.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f003.planTitulo')}>{seccion('plan', 'pla', CAMPOS_PLAN_003, 2, 8)}</Bloque>

      <Bloque titulo={t('formularios.f003.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
